import React, { useState, useMemo } from 'react';
import { ArrowUpDown, ExternalLink } from 'lucide-react';
import { useNavigate } from 'react-router-dom';

const STATUS_BADGE = {
  Green:  'bg-green-100 text-green-800',
  Yellow: 'bg-yellow-100 text-yellow-800',
  Red:    'bg-red-100 text-red-800',
};

const COLUMNS = [
  { key: 'ppf_file_name',        label: 'POLICY',       width: '160px' },
  { key: 'ppf_url',         label: 'PPU',           width: '200px' },
  { key: 'scraper_date',    label: 'SCRAPED',       width: '110px' },
  { key: 'status',          label: 'STATUS',        width: '90px'  },
  { key: 'decision',        label: 'DECISION',      width: '120px' },
  { key: 'ai_version',      label: 'AI VER.',       width: '80px'  },
  { key: 'assessment_date', label: 'ASSESS DATE',   width: '110px' },
];

export const PoliciesTable = ({ data = [], total, onDecisionClick, tableHeight = '750px' }) => {
  const navigate = useNavigate();
  const [sortConfig, setSortConfig] = useState({ key: null, direction: 'asc' });

  const sortedData = useMemo(() => {
    if (!sortConfig.key) return data;
    return [...data].sort((a, b) => {
      const aValue = a[sortConfig.key] ?? '';
      const bValue = b[sortConfig.key] ?? '';
      if (typeof aValue === 'string') {
        return sortConfig.direction === 'asc' ? aValue.localeCompare(bValue) : bValue.localeCompare(aValue);
      } else {
        return sortConfig.direction === 'asc' ? aValue - bValue : bValue - aValue;
      }
    });
  }, [data, sortConfig]);

  const handleSort = (key) => {
    setSortConfig(prev => ({
      key,
      direction: prev.key === key && prev.direction === 'asc' ? 'desc' : 'asc',
    }));
  };

  return (
    <div>
      <div className="mb-2">
        <h3 className="text-base font-semibold text-gray-900">Privacy Policy Documents</h3>
        <p className="text-sm text-gray-500">
          Privacy policy documents and assessment details ({(total ?? data.length).toLocaleString()} total)
        </p>
      </div>
      <div
        className="overflow-y-auto border rounded-lg"
        style={{ maxHeight: tableHeight }}
      >
      <table className="w-full">
        <thead className="bg-gray-50 sticky top-0 z-10">
          <tr className="border-b border-gray-200">
            {COLUMNS.map(col => (
              <th
                key={col.key}
                className="text-left text-xs font-medium text-gray-500 uppercase tracking-wider py-3 px-2"
                style={{ width: col.width }}
              >
                <div className="flex items-center gap-1 cursor-pointer" onClick={() => handleSort(col.key)}>
                  {col.label}
                  <ArrowUpDown
                    className={`w-3 h-4 ${sortConfig.key === col.key ? 'text-red-500 rotate-180' : 'text-gray-400'}`}
                  />
                </div>
              </th>
            ))}
          </tr>
        </thead>

        <tbody>
          {data.length === 0 ? (
            <tr>
              <td colSpan={COLUMNS.length} className="px-4 py-8 text-center text-gray-400">No documents found</td>
            </tr>
          ) : (
            sortedData.map(row => (
              <tr key={row.id} className="border-b border-gray-100 hover:bg-gray-50">
                <td className="py-4 px-2 font-mono text-xs" style={{ width: '160px' }}>
                  <div className="flex items-center gap-1.5">
                    <button
                      onClick={() => navigate(`/policy?policy=${encodeURIComponent(row.ppf_checksum)}&ppf_file=${encodeURIComponent(row.ppf_file_name)}`)}
                      className="text-blue-600 hover:underline text-left truncate"
                      title={row.ppf_file_name}
                    >
                      {row.ppf_file_name}
                    </button>
                    <button
                      onClick={() => window.open(`/policy?policy=${encodeURIComponent(row.ppf_checksum)}&ppf_file=${encodeURIComponent(row.ppf_file_name)}`, '_blank')}
                      className="text-gray-400 hover:text-blue-600 transition-colors shrink-0"
                      title="Open in new tab"
                    >
                      <ExternalLink className="w-3 h-3" />
                    </button>
                  </div>
                </td>

                <td className="py-4 px-2 max-w-[200px] truncate" style={{ width: '200px' }}>
                  <button
                    onClick={() => navigate(`/policy?policy=${encodeURIComponent(row.ppf_checksum)}`)}
                    className="text-blue-600 hover:underline text-xs font-mono text-left truncate block w-full"
                    title={row.ppf_url}
                  >
                    {row.ppf_url}
                  </button>
                </td>

                <td className="py-4 px-2 text-gray-700 text-sm whitespace-nowrap" style={{ width: '110px' }}>
                  {row.scraper_date?.split('T')[0]}
                </td>

                <td className="py-4 px-2" style={{ width: '90px' }}>
                  <span className={`px-2 py-0.5 rounded text-xs font-medium ${STATUS_BADGE[row.status] || 'bg-gray-100 text-gray-700'}`}>
                    {row.status}
                  </span>
                </td>

                <td
                  className="py-4 px-2 whitespace-nowrap cursor-pointer"
                  style={{ width: '120px' }}
                  onClick={() => onDecisionClick?.(row)}
                  title="Click to update decision"
                >
                  <span className="text-blue-600 hover:underline text-sm">
                    {row.decision ?? '—'}
                  </span>
                </td>

                <td className="py-4 px-2 text-gray-700 text-sm" style={{ width: '80px' }}>
                  {row.ai_version}
                </td>

                <td className="py-4 px-2 text-gray-700 text-sm whitespace-nowrap" style={{ width: '110px' }}>
                  {row.assessment_date}
                </td>
              </tr>
            ))
          )}
        </tbody>
      </table>
      </div>
    </div>
  );
};
